import useTranslation from 'next-translate/useTranslation';
import { Box, Center, createStyles, Switch, useMantineTheme } from '@mantine/core';

import AppBanners from '~/domains/common/components/AppBanners/AppBanners';
import Filters from '~/domains/common/components/Filters/Filters';
import { Text } from '~/domains/design-system/Text';
import SubHeader from '~/domains/common/components/AppShellHeader/components/Subheader/SubHeader';
import { useWelcomeModal, WelcomeModalType } from '~/domains/common/hooks/useWelcomeModal';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import isWebView from '~/utils/isWebView';

import MyLayout from '../components/MyLayout';
import MyContestsSkeleton from '../components/MyContestsSkeleton';
import MyContestsError from '../components/MyContestsError';
import MyContestsConnectionError from '../components/MyContestsConnectionError';
import MyContestsEmpty from '../components/MyContestsEmpty';
import MyContests from '../components/MyContests';
import useEntriesTabsProps from '../hooks/useEntriesTabsProps';
import useEntriesPageData from '../hooks/useEntriesPageData';
import useEntriesFiltersProps from '../hooks/useEntriesFiltersProps';
import MyContestInvites from '../components/MyContestInvites';
import MyContestInvitesError from '../components/MyContestInvitesError';
import EntriesCallout from '../components/EntriesCallout';

const useStyles = createStyles(() => ({
  showEliminatedToggle: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'center',
    height: 0,
    marginLeft: 'auto',
  },
}));

function EntriesPage() {
  const { t } = useTranslation('contest');
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const isWebViewValue = isWebView();

  const tabsProps = useEntriesTabsProps();
  const {
    contestsData,
    invitesData,
    isInvitesTab,
    contestStatusFilter,
    contests,
    isToggleVisible,
    showEliminated,
    onShowEliminatedChange,
  } = useEntriesPageData();

  const currentData = isInvitesTab ? invitesData : contestsData;
  const { isLoading, isError, itemsTotal, fetchNextPage } = currentData;
  // Invites tab doesn't have a working pagination, so disabling infinite loader with this simple trick
  const hasNextPage = isInvitesTab ? false : currentData.hasNextPage;
  const currentItems = isInvitesTab ? invitesData.items : contests;
  const itemCount = currentItems?.length || 0;

  const filtersProps = useEntriesFiltersProps({ status: contestStatusFilter });

  useWelcomeModal(WelcomeModalType.MY_CONTESTS, !isWebViewValue);

  const { loaderRef } = useInfiniteScroll({
    hasMore: hasNextPage,
    fetchMore: fetchNextPage,
  });

  const renderMainContent = () => {
    if (isLoading) return <MyContestsSkeleton />;
    if (isError) {
      if (isInvitesTab) {
        return <MyContestInvitesError onRetry={() => invitesData.refetch()} />;
      }
      if (contestsData.isConnectionError) {
        return <MyContestsConnectionError onRetry={() => contestsData.refetch()} />;
      }
      return <MyContestsError />;
    }

    if (itemCount === 0) {
      return <MyContestsEmpty contestTab={tabsProps.value} />;
    }

    return isInvitesTab ? (
      <MyContestInvites invites={invitesData.items || []} />
    ) : (
      <MyContests
        contests={contests}
        contestTab={tabsProps.value}
        key={`contestTab-${tabsProps.value}`}
      />
    );
  };

  const shouldShowLoadMore = hasNextPage && !isLoading && itemCount > 0;
  const shouldShowItemCount = !isInvitesTab && itemCount > 0;

  return (
    <>
      <SubHeader title={t('myContests.header.title')} subMenuItems={tabsProps.tabs} />
      <AppBanners />
      <MyLayout>
        <MyLayout.Content>
          <EntriesCallout />

          <Filters {...filtersProps} hide={isInvitesTab}>
            {isToggleVisible && (
              <Box className={classes.showEliminatedToggle}>
                <Switch
                  id="entries-show-eliminated"
                  size="md"
                  label={
                    <Text tag="span" variant="subhead-medium">
                      {t('myContests.filters.showEliminated')}
                    </Text>
                  }
                  labelPosition="left"
                  checked={showEliminated}
                  onChange={(event) => onShowEliminatedChange(event.currentTarget.checked)}
                />
              </Box>
            )}
          </Filters>

          {renderMainContent()}

          <Box ref={loaderRef} mt={theme.spacing.md}>
            {shouldShowLoadMore && <MyContestsSkeleton numberOfSkeletons={3} />}
          </Box>

          {shouldShowItemCount && (
            <Center mt="xl">
              <Text variant="body-large" c="gray.6">
                {t('myContests.itemsCount', {
                  count: itemCount,
                  total: itemsTotal,
                })}
              </Text>
            </Center>
          )}
        </MyLayout.Content>
      </MyLayout>
    </>
  );
}

export default EntriesPage;
